import { EVMAnalyticsEvent } from "@aix/vidmax-shared/src/analytics"
import type { ESubscriptionTier } from "@aix/vidmax-shared/src/constants/enums"
import type { EFeaturesVidmax } from "@aix/vidmax-shared/src/constants/pricing"
import { _checkUserFeatureAccess } from "@aix/vidmax-shared/src/constants/pricing"
import dayjs from "dayjs"
import { calcSubscriptionState } from "@aix/vidmax-shared/src/utils/stripe"
import { useCallback, useEffect, useMemo, useState, lazy, Suspense } from "react"
import type { PropsWithChildren } from "react"
import { useAuthSession } from "~/hooks/useAuthSession"
import { api } from "~/utils/api"
import { usePH } from "~/utils/posthogClient"
import { FreemiumModalContext } from "./useFreemiumModalContext"

// Lazy load FreemiumModals to avoid loading Stripe/ComparisonPricingTable at root
// This component includes pricing tables and Stripe integration that's only needed on upsell
const FreemiumModals = lazy(() => import("~/components/Payment/FreemiumModals"))

export function FreemiumModalProvider({ children }: PropsWithChildren) {
  const [notEnoughCreditsErrorString, setNotEnoughCreditsErrorString] = useState("")
  const [upgradeToProString, setUpgradeToProString] = useState("")
  const [cardPastDueErrorString, setCardPastDueErrorString] = useState("")
  const [requiredFeatureAccess, setRequiredFeatureAccess] = useState<{
    feature: EFeaturesVidmax
    requiredSubscriptionType: ESubscriptionTier
  }>()
  const { capture } = usePH()
  const { session } = useAuthSession()

  const { data: user } = api.users.getUserWithTeam.useQuery(undefined, {
    enabled: !!session?.user?.id,
  })

  const { isActive, subscriptionMeta, subscriptionType } = useMemo(() => {
    return calcSubscriptionState(user?.currentTeam)
  }, [user?.currentTeam])

  useEffect(() => {
    if (notEnoughCreditsErrorString) {
      capture(EVMAnalyticsEvent.NOT_ENOUGH_CREDITS_UPSELL, {
        setOnce: {
          first_upsell_at: dayjs().toISOString(),
        },
        add: {
          upsell_shown: 1,
        },
      })
    }
  }, [notEnoughCreditsErrorString])
  useEffect(() => {
    if (upgradeToProString) {
      capture(EVMAnalyticsEvent.PRO_FEATURE_UPSELL, {
        feature: requiredFeatureAccess?.feature || "upgrade_click",
        planRequired: requiredFeatureAccess?.requiredSubscriptionType,
        setOnce: {
          first_upsell_at: dayjs().toISOString(),
        },
        add: {
          upsell_shown: 1,
        },
      })
    }
  }, [upgradeToProString])

  /**
   * Checks if the user has access to a feature and shows the upgrade modal if they don't
   */
  const checkFeatureAccessAndShowUpgrade = useCallback(
    (feature: EFeaturesVidmax) => {
      const { hasAccess, subscriptionNeeded, featureName } = _checkUserFeatureAccess(feature, subscriptionType)

      if (!hasAccess) {
        setRequiredFeatureAccess({ feature, requiredSubscriptionType: subscriptionNeeded })
        setUpgradeToProString(`${featureName} requires ${subscriptionNeeded} subscription`)
      }

      return hasAccess
    },
    [isActive, subscriptionMeta],
  )

  // Only render modals when they're actually needed (triggered by user action)
  const shouldRenderModals = notEnoughCreditsErrorString || cardPastDueErrorString || upgradeToProString

  return (
    <FreemiumModalContext.Provider
      value={{
        notEnoughCreditsErrorString,
        setNotEnoughCreditsErrorString,
        cardPastDueErrorString,
        setCardPastDueErrorString,
        requiredFeatureAccess,
        checkFeatureAccessAndShowUpgrade,
        setUpgradeToProString,
        upgradeToProString,
      }}
    >
      {children}
      {/* Lazy load FreemiumModals only when triggered - saves ~200KB+ initial bundle */}
      {shouldRenderModals && (
        <Suspense fallback={null}>
          <FreemiumModals
            notEnoughCreditsErrorString={notEnoughCreditsErrorString}
            setNotEnoughCreditsErrorString={setNotEnoughCreditsErrorString}
            cardPastDueErrorString={cardPastDueErrorString}
            setCardPastDueErrorString={setCardPastDueErrorString}
            requiredFeatureAccess={requiredFeatureAccess}
            setUpgradeToProString={setUpgradeToProString}
            upgradeToProString={upgradeToProString}
          />
        </Suspense>
      )}
    </FreemiumModalContext.Provider>
  )
}
